import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import SnackBar from 'components/common/snack-bar'
import useIntersection from 'components/custom-hooks/useIntersection'
import env from 'config/env'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import { getAuthStatus, throttleCallback } from 'lib/utils/common/commonUtils'
import { hasLocizeTranslation } from 'lib/utils/locize'
import usePomeloCashbackEnable from 'pages/user/pomelo-credit/credit-cashback/usePomeloCashbackEnable'
import Accordion from 'components/common/accordion'
import { segmentTrackPDPInteract } from 'lib/utils/product'
import { PDP_INTERACT_ELEM_EVENT_NAME } from 'components/pdp/const'
import getLoyaltyUser from 'lib/api/loyalty'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import AddToBagCTA from './add-to-bag-cta'
import { PDP_LOCIZE } from './const'
import KlinkChatCTA from './klink-chat-cta'
import ProductMeasurements from './measurements'
import OptionsSelector from './options-selector'
import styles from './styles.scss'
import SoldOutRecovery from './sold-out-recovery'
import getActualInfoContainerWidth from './utils'
import ProductDetails from '../information/descriptions/product-details'
import AddToBagModal from './add-to-bag-modal/AddToBagModal'
import ProductStyleGallery from '../style-gallery/product-style-gallery/ProductStyleGallery'
import ProductAvailableSizes from './product-available-sizes/ProductAvailableSizes'
import FindThisItemAtStoreCta from './find-this-item-at-store-cta'
import ProductVirtualTryOn from '../product-virtual-try-on/ProductVirtualTryOn'
import { MEASUREMENT_DATA } from './measurements/const'
import MobilePurchaseBar from '../mobile-buying-flow/MobilePurchaseBar'
import ProductUSP from './product-usp'

const ProductInteractions = ({
  auth,
  cartQuantity,
  internationalization,
  isCoachMarkClosed,
  isPhone,
  product,
  setWaitlistSnackBarOpen,
  showFallback,
  t,
  isMobileBuyingFlow,
  isDeliverySummary,
  isVirtualTryOn,
  onShowShippingDetails,
  shareAction,
}) => {
  const initSelectedOption =
    product?.attributes?.length === 1 ? product.attributes[0] : null
  const isCashbackEnable = usePomeloCashbackEnable(internationalization.shop)
  const [selectedOption, setSelectedOption] = useState(initSelectedOption)
  const [localWaitlists, setLocalWaitlists] = useState({})
  const [optionMessage, setOptionMessage] = useState('')
  const [isOpenProductMeasurements, setIsOpenProductMeasurements] =
    useState(false)
  const [actualInfoContainerWidth, setActualInfoContainerWidth] = useState(0)
  const [loyaltyData, setLoyaltyData] = useState(null)
  const optionsRef = useRef(null)
  const sizeGuideId = `pdp-inline-size-guide-${product.id_product}`
  const isDesktopPurchaseFlow = !isPhone && !isMobileBuyingFlow
  const { isFetched, isLoyaltyMember, isGuest } = getAuthStatus(auth)
  const trackAccordionClick = (isAccordionOpen, accordionName) =>
    isAccordionOpen &&
    segmentTrackPDPInteract(
      product,
      internationalization.country,
      PDP_INTERACT_ELEM_EVENT_NAME[accordionName],
    )

  const isShowEarnCashbackPDP = Boolean(
    isCashbackEnable && product.cashback?.cashback_earned,
  )
  const cashbackText = isLoyaltyMember
    ? PDP_LOCIZE.PDP_EARN_CASHBACK_PERK
    : PDP_LOCIZE.PDP_EARN_CASHBACK_NON_PERK
  const ctaRef = useRef()
  const isIntersect = useIntersection(ctaRef, {
    rootMargin: '0px 0px -5px 0px',
    threshold: 1,
    once: false,
  })
  const isIndoShop = internationalization.country === 'id'
  const isThShop = internationalization.country === 'th'
  const isLoyaltyPink = loyaltyData?.current_tier_id === 1
  const isShowDeliveryOptions = !isIndoShop
  const deliveryText =
    isThShop && isLoyaltyPink && isGuest
      ? hasLocizeTranslation(t, 'DELIVERY_TH_PINK', 'Free delivery over ฿1000')
      : hasLocizeTranslation(t, 'DELIVERY_ALL_SHOP', 'Free delivery')

  // @TODO: API develop new flag to identified gift card product type
  const giftCardCategoryId = env.ENVIRONMENT === 'production' ? 4044 : 2175
  const isGiftCardType = product?.category.id_category === giftCardCategoryId
  const hasMeasurement = !!product?.attributes[0]?.measurements?.length
  const shouldShowProductMeasurement = hasMeasurement && !isGiftCardType
  const invokeSetSelectedOption = (option) => {
    setSelectedOption(option)
    const isNewSize = product?.is_new_size_method || false
    const newSizeMessageText = hasLocizeTranslation(
      t,
      PDP_LOCIZE.PDP_SIZE_OPTIONS_MESSAGE.key,
      PDP_LOCIZE.PDP_SIZE_OPTIONS_MESSAGE.text,
    )
    setOptionMessage(isNewSize ? newSizeMessageText : '')
  }

  const handleOpenProductMeasurements = (isOpen) => {
    setIsOpenProductMeasurements(isOpen)
  }

  const toggleInlineSizeGuide = () => {
    const nextOpen = !isOpenProductMeasurements
    setIsOpenProductMeasurements(nextOpen)
    trackAccordionClick(nextOpen, 'sizeGuide')
  }

  const focusSizeSelection = () => {
    optionsRef.current
      ?.querySelector('.pdp__options-item')
      ?.focus({ preventScroll: true })
  }

  const updateSnackBarWidth = useCallback(() => {
    const newWidth = getActualInfoContainerWidth(isPhone)
    if (newWidth !== actualInfoContainerWidth) {
      setActualInfoContainerWidth(newWidth)
    }
  }, [actualInfoContainerWidth, isPhone])

  useEffect(() => {
    const shouldFetchLoyalty = auth?.customerData?.isFetched

    const fetchLoyaltyData = async () => {
      try {
        if (!isGuest) {
          const res = await getLoyaltyUser()
          setLoyaltyData(res)
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error(e)
      }
    }

    if (shouldFetchLoyalty) {
      fetchLoyaltyData()
    }
  }, [auth, isGuest])

  useEffect(() => {
    if (selectedOption !== initSelectedOption) {
      invokeSetSelectedOption(initSelectedOption)
    } else {
      setOptionMessage('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id_product])

  useEffect(() => {
    if (isMobileBuyingFlow) setIsOpenProductMeasurements(false)
  }, [product.id_product, isMobileBuyingFlow])

  useEffect(() => {
    updateSnackBarWidth()
    window.addEventListener(
      'resize',
      throttleCallback(updateSnackBarWidth, 100),
      true,
    )

    return () => {
      window.removeEventListener('resize', updateSnackBarWidth)
    }
  }, [updateSnackBarWidth])

  const CTASComponent = (
    <div
      className={clsx('pdp__ctas-container', {
        'cashback-display': isShowEarnCashbackPDP,
        'is-sticky': !isIntersect,
      })}
      ref={ctaRef}
    >
      <KlinkChatCTA />
      {isPhone && !isMobileBuyingFlow ? (
        <AddToBagModal
          localWaitlists={localWaitlists}
          product={product}
          selectedOption={selectedOption}
          setLocalWaitlists={setLocalWaitlists}
          setOptionMessage={setOptionMessage}
          showFallback={showFallback}
          setWaitlistSnackBarOpen={setWaitlistSnackBarOpen}
          onSelected={invokeSetSelectedOption}
          cashbackText={cashbackText}
          isShowEarnCashbackPDP={isShowEarnCashbackPDP}
          isFetched={isFetched}
        />
      ) : (
        <AddToBagCTA
          openBagOnSuccess={isMobileBuyingFlow || isDesktopPurchaseFlow}
          selectSizeLabel={
            isMobileBuyingFlow
              ? hasLocizeTranslation(t, 'PDP_SELECT_SIZE_TEXT', 'Select a size')
              : undefined
          }
          onSelectSize={isMobileBuyingFlow ? focusSizeSelection : undefined}
          localWaitlists={localWaitlists}
          product={product}
          selectedOption={selectedOption}
          setLocalWaitlists={setLocalWaitlists}
          setOptionMessage={setOptionMessage}
          showFallback={showFallback}
          t={t}
          setWaitlistSnackBarOpen={setWaitlistSnackBarOpen}
          cashbackText={cashbackText}
          isShowEarnCashbackPDP={isShowEarnCashbackPDP}
          isFetched={isFetched}
        />
      )}
    </div>
  )

  return (
    <Fragment>
      <style jsx>{styles}</style>
      <div className="pdp__interactions-container" ref={optionsRef}>
        {(!isPhone || isMobileBuyingFlow) && (
          <OptionsSelector
            hideStoreCta={isMobileBuyingFlow || isDesktopPurchaseFlow}
            showSelectedSize={isMobileBuyingFlow}
            sizeGuideAction={
              shouldShowProductMeasurement ? (
                <button
                  type="button"
                  className={clsx('pdp-inline-size-guide-link', Typo.caption)}
                  onClick={toggleInlineSizeGuide}
                  aria-expanded={isOpenProductMeasurements}
                  aria-controls={sizeGuideId}
                >
                  {hasLocizeTranslation(
                    t,
                    MEASUREMENT_DATA.sizeGuideTitle.titleKey,
                    MEASUREMENT_DATA.sizeGuideTitle.default,
                  )}
                </button>
              ) : null
            }
            localWaitlists={localWaitlists}
            onSelected={invokeSetSelectedOption}
            optionMessage={optionMessage}
            setOptionMessage={setOptionMessage}
            product={product}
            selectedOption={selectedOption}
            isPhone={isPhone}
            isCoachMarkClosed={isCoachMarkClosed}
          />
        )}
        {(isMobileBuyingFlow || isDesktopPurchaseFlow) &&
          shouldShowProductMeasurement &&
          isOpenProductMeasurements && (
            <div id={sizeGuideId} className="pdp-inline-measurements">
              <ProductMeasurements
                isExpandedAccordion={isOpenProductMeasurements}
                product={product}
                onOpenProductMeasurements={handleOpenProductMeasurements}
              />
            </div>
          )}
      </div>
      {isMobileBuyingFlow ? (
        <MobilePurchaseBar>{CTASComponent}</MobilePurchaseBar>
      ) : (
        CTASComponent
      )}
      {!isMobileBuyingFlow && isShowDeliveryOptions && (
        <div className="pdp__interactions-delivery-container">
          <div className="pdp__interactions-delivery">
            <Icon src={ICONS.deliveryUSP} size={ICON_SIZE.smallMedium} />
            <span className={Typo.caption}>{deliveryText}</span>
          </div>
        </div>
      )}
      {!isGiftCardType && (!isPhone || isMobileBuyingFlow) && (
        <SoldOutRecovery product={product} selectedOption={selectedOption} />
      )}
      {isMobileBuyingFlow && (
        <Fragment>
          {isShowDeliveryOptions && !isDeliverySummary && (
            <div className="pdp__interactions-delivery-container">
              <div className="pdp__interactions-delivery">
                <button
                  type="button"
                  className={clsx('pdp-mobile-shipping-link', Typo.caption)}
                  onClick={onShowShippingDetails}
                >
                  <Icon src={ICONS.deliveryUSP} size={ICON_SIZE.smallMedium} />
                  {hasLocizeTranslation(
                    t,
                    'PDP_ACCORDION_SHIPPING_RETURNS',
                    'Shipping & Returns',
                  )}
                </button>
              </div>
            </div>
          )}
          <div className="pdp-mobile-buying-services">
            {product.virtual_try_on_enabled && (
              <ProductVirtualTryOn
                compact
                isPhone={isPhone}
                productId={product.id_product}
                isVirtualTryOn={isVirtualTryOn}
              />
            )}
            <FindThisItemAtStoreCta
              product={product}
              selectedOption={selectedOption}
              setOptionMessage={setOptionMessage}
            />
            {shareAction}
          </div>
          {isDeliverySummary && <ProductUSP compact policyFirst />}
        </Fragment>
      )}
      {isDesktopPurchaseFlow && (
        <div className="pdp-desktop-buying-services">
          {product.virtual_try_on_enabled && (
            <ProductVirtualTryOn
              compact
              isPhone={isPhone}
              productId={product.id_product}
              isVirtualTryOn={isVirtualTryOn}
            />
          )}
          <FindThisItemAtStoreCta
            product={product}
            selectedOption={selectedOption}
            setOptionMessage={setOptionMessage}
          />
          {shareAction}
        </div>
      )}
      {isPhone && !isMobileBuyingFlow && (
        <ProductAvailableSizes
          attributes={product?.attributes}
          is_coming_soon={product?.is_coming_soon}
        />
      )}
      {isPhone && !isMobileBuyingFlow && (
        <div id="pdp-reviews-containers" data-cy="pdp-reviews-containers">
          <ProductStyleGallery productId={product?.id_product} />
        </div>
      )}
      <div
        className={clsx('pdp-snackbar__overlay', Typo.caption)}
        style={{
          ...(actualInfoContainerWidth && {
            width: `${actualInfoContainerWidth}px`,
          }),
        }}
      >
        <SnackBar
          className="pdp-body__snackbar"
          open={
            internationalization.shop === 5 && cartQuantity?.total_items === 20
          }
        >
          <div className="pdp-max-quantity-s2s">
            {hasLocizeTranslation(
              t,
              PDP_LOCIZE.PDP_ITEM_LIMIT_WARNING_MESSAGE.key,
              PDP_LOCIZE.PDP_ITEM_LIMIT_WARNING_MESSAGE.text,
            )}
          </div>
        </SnackBar>
      </div>
      {product?.description && (
        <div className="pdp__interactions-size">
          <Accordion
            titleTypography={Typo.subtitle2}
            title={hasLocizeTranslation(t, 'PDP_ACCORDION_DETAILS', 'Details')}
            onChange={(isAccordionOpen) =>
              trackAccordionClick(isAccordionOpen, 'detailView')
            }
          >
            <ProductDetails
              details={product.description}
              productID={product.id_product}
            />
          </Accordion>
        </div>
      )}
      {shouldShowProductMeasurement &&
        !isMobileBuyingFlow &&
        !isDesktopPurchaseFlow && (
          <div className="pdp__interactions-size">
            <ProductMeasurements
              isExpandedAccordion={isOpenProductMeasurements}
              product={product}
              onOpenProductMeasurements={handleOpenProductMeasurements}
            />
          </div>
        )}
    </Fragment>
  )
}

ProductInteractions.defaultProps = {
  isMobileBuyingFlow: false,
  isDeliverySummary: false,
  isVirtualTryOn: false,
  onShowShippingDetails: null,
  isCoachMarkClosed: false,
  cartQuantity: null,
  shareAction: null,
}

ProductInteractions.propTypes = {
  shareAction: PropTypes.node,
  isMobileBuyingFlow: PropTypes.bool,
  isDeliverySummary: PropTypes.bool,
  isVirtualTryOn: PropTypes.bool,
  onShowShippingDetails: PropTypes.func,
  auth: PropTypes.shape({
    customerData: PropTypes.shape({
      loyalty_signup: PropTypes.number,
      isFetched: PropTypes.bool,
    }),
    isGuestMode: PropTypes.bool,
  }).isRequired,
  cartQuantity: PropTypes.shape({
    total_items: PropTypes.number,
  }),
  internationalization: PropTypes.shape({
    country: PropTypes.string,
    shop: PropTypes.number,
  }).isRequired,
  isCoachMarkClosed: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    virtual_try_on_enabled: PropTypes.bool,
    description: PropTypes.string,
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        measurements: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
    is_coming_soon: PropTypes.bool,
    cashback: PropTypes.shape({
      cashback_earned: PropTypes.number,
      cashback_earned_formatted: PropTypes.string,
    }),
    category: PropTypes.shape({
      id_category: PropTypes.number,
    }),
    id_product: PropTypes.number.isRequired,
    is_new_size_method: PropTypes.bool,
    is_on_wishlist: PropTypes.bool.isRequired,
  }).isRequired,
  setWaitlistSnackBarOpen: PropTypes.func.isRequired,
  showFallback: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

const Extended = withI18next()(ProductInteractions)

export default connect((state) => ({
  cartQuantity: state.cartBadge.payload,
  isPhone: state.device.isPhone,
  internationalization: state.internationalization,
  auth: state.auth,
}))(Extended)
